import { Button } from 'Assessment/src/components/ui/button';
import { Card, CardContent } from 'Assessment/src/components/ui/card';
import { Progress } from 'Assessment/src/components/ui/progress';
import {  Mic, Square, Play, Pause, RotateCcw } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { RerecordWarningDialog } from 'Assessment/src/components/RerecordWarningDialog';
import { toast } from 'sonner';
import { useColor } from "components/ui/ColorContext";

interface AudioRecorderProps {
  onRecordingComplete: (audioBlob: Blob) => void;
  onStopRecording?: (audioBlob: Blob) => void;
  onRecordingStart?: () => void;
  onRecordingStateChange?: (isRecording: boolean) => void;
  maxDuration?: number; // in seconds
  disabled?: boolean;
  attemptCount?: number;
  isValidating?: boolean;
  hideRerecord?: boolean;
}

export const AudioRecorder = ({ 
  onRecordingComplete,
  onStopRecording,
  onRecordingStart,
  onRecordingStateChange,
  maxDuration = 60,
  disabled = false,
  attemptCount = 1,
  isValidating = false,
  hideRerecord = false
}: AudioRecorderProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const { primaryColor } = useColor();
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    // Call onRecordingStart callback if provided
    if (onRecordingStart) {
      onRecordingStart();
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Try to use audio/wav for better compatibility, fallback to default
      let mediaRecorder: MediaRecorder;
      try {
        mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      } catch (e) {
        // Fallback to default format if webm not supported
        mediaRecorder = new MediaRecorder(stream);
      }
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        
        const mimeType = mediaRecorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setAudioURL(url);
        onRecordingComplete(blob);
        
        // Trigger auto-transcription if callback provided
        if (onStopRecording) {
          onStopRecording(blob);
        }
        
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Notify parent component about recording state
      if (onRecordingStateChange) {
        onRecordingStateChange(true);
      }

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1;
          if (newTime >= maxDuration) {
            stopRecording();
            return maxDuration;
          }
          return newTime;
        });
      }, 1000);

      toast.success('Recording started');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Failed to access microphone. Please check permissions.');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      toast.info('Recording paused');
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1;
          if (newTime >= maxDuration) {
            stopRecording();
            return maxDuration;
          }
          return newTime;
        });
      }, 1000);
      
      toast.info('Recording resumed');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
     
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      // Notify parent component about recording state
      if (onRecordingStateChange) {
        onRecordingStateChange(false);
      }
      
      toast.success('Recording stopped');
    }
  };

  const handleRerecordClick = () => {
    // Show warning if this is the second attempt (about to start third)
    if (attemptCount === 2) {
      setShowWarningDialog(true);
    } else {
      resetRecording();
    }
  };

  const resetRecording = () => {
    setAudioURL(null);
    setRecordingTime(0);
    chunksRef.current = [];
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage = (recordingTime / maxDuration) * 100;

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between text-lg">
            <span className="text-muted-foreground">
              {isRecording ? 'Recording...' : audioURL ? 'Recording complete' : 'Ready to record'}
            </span>
            <span className="font-medium">
              {formatTime(recordingTime)} / {formatTime(maxDuration)}
            </span>
          </div>
          {/* <Progress value={progressPercentage} className="h-2" /> */}
            <Progress
  value={progressPercentage}
  className="h-2 bg-gray-400"  
/>

        </div>

        {audioURL && (
          <div className="space-y-2">
            <audio src={audioURL} controls className="w-full" />
          </div>
        )}

      

<div className="flex gap-2 justify-center">
          {!isRecording && !audioURL && (
             <Button
      onClick={startRecording}
      disabled={disabled}
      size="lg"
      style={{
    background: `
      linear-gradient(
        135deg,
        ${primaryColor}55 0%,
        ${primaryColor}33 35%,
        rgba(0, 0, 0, 0.25) 70%,
        rgba(0, 0, 0, 0.45) 100%
      )
    `,
    borderColor: `${primaryColor}aa`,
    boxShadow: `
      0 0 0 1px ${primaryColor}66,
      0 0 18px ${primaryColor}88,
      inset 0 0 14px ${primaryColor}33
    `,
  }}
className="
  gap-2 border
  text-white 
   text-xl
    font-semibold
  backdrop-blur-xl
  hover:opacity-90 transition-all
  disabled:opacity-40 disabled:cursor-not-allowed
"

    >
      <Mic className="h-5 w-5" />
      Start Recording
    </Button>
          )}
 

          {isRecording && !isPaused && (
            <>
              <Button
                onClick={pauseRecording}
                variant="secondary"
                size="lg"
                className="gap-2  text-xl
    font-semibold"
                style={{
    background: `
      linear-gradient(
        135deg,
        ${primaryColor}55 0%,
        ${primaryColor}33 35%,
        rgba(0, 0, 0, 0.25) 70%,
        rgba(0, 0, 0, 0.45) 100%
      )
    `,
    borderColor: `${primaryColor}aa`,
    boxShadow: `
      0 0 0 1px ${primaryColor}66,
      0 0 18px ${primaryColor}88,
      inset 0 0 14px ${primaryColor}33
    `,
  }}
              >
                <Pause className="h-5 w-5" />
                Pause
              </Button>
              <Button
                onClick={stopRecording}
                variant="destructive"
                size="lg"
                className="gap-2 glow-button-red  text-xl
    font-semibold"
                style={{
    background: `
      linear-gradient(
        135deg,
        ${primaryColor}55 0%,
        ${primaryColor}33 35%,
        rgba(0, 0, 0, 0.25) 70%,
        rgba(0, 0, 0, 0.45) 100%
      )
    `,
    borderColor: `${primaryColor}aa`,
    boxShadow: `
      0 0 0 1px ${primaryColor}66,
      0 0 18px ${primaryColor}88,
      inset 0 0 14px ${primaryColor}33
    `,
  }}
              >
                <Square className="h-5 w-5" />
                Stop
              </Button>
            </>
          )}

          {isRecording && isPaused && (
            <>
              <Button
                onClick={resumeRecording}
                size="lg"
                className="gap-2  text-xl
    font-semibold"
                style={{
    background: `
      linear-gradient(
        135deg,
        ${primaryColor}55 0%,
        ${primaryColor}33 35%,
        rgba(0, 0, 0, 0.25) 70%,
        rgba(0, 0, 0, 0.45) 100%
      )
    `,
    borderColor: `${primaryColor}aa`,
    boxShadow: `
      0 0 0 1px ${primaryColor}66,
      0 0 18px ${primaryColor}88,
      inset 0 0 14px ${primaryColor}33
    `,
  }}
              >
                <Play className="h-5 w-5" />
                Continue
              </Button>
              <Button
                onClick={stopRecording}
                variant="destructive"
                size="lg"
                className="gap-2 glow-button-red"
                style={{
    background: `
      linear-gradient(
        135deg,
        ${primaryColor}55 0%,
        ${primaryColor}33 35%,
        rgba(0, 0, 0, 0.25) 70%,
        rgba(0, 0, 0, 0.45) 100%
      )
    `,
    borderColor: `${primaryColor}aa`,
    boxShadow: `
      0 0 0 1px ${primaryColor}66,
      0 0 18px ${primaryColor}88,
      inset 0 0 14px ${primaryColor}33
    `,
  }}
              >
                <Square className="h-5 w-5" />
                Stop
              </Button>
            </>
          )}

          {audioURL && !hideRerecord && attemptCount < 3 && !isValidating && (
           
             <Button
  onClick={handleRerecordClick}
  size="lg"
  disabled={disabled}
  style={{
    background: `
      linear-gradient(
        135deg,
        ${primaryColor}55 0%,
        ${primaryColor}33 35%,
        rgba(0, 0, 0, 0.25) 70%,
        rgba(0, 0, 0, 0.45) 100%
      )
    `,
    borderColor: `${primaryColor}aa`,
    boxShadow: `
      0 0 0 1px ${primaryColor}66,
      0 0 18px ${primaryColor}88,
      inset 0 0 14px ${primaryColor}33
    `,
  }}
className="
  gap-2 border
  text-white 
   text-xl
    font-semibold
  backdrop-blur-xl
  hover:opacity-90 transition-all
  disabled:opacity-40 disabled:cursor-not-allowed
"

>
  <RotateCcw className="h-5 w-5" />
  Re-record
</Button>
          )}
        </div>

        <RerecordWarningDialog 
          open={showWarningDialog}
          onCancel={() => setShowWarningDialog(false)}
          onContinue={() => {
            setShowWarningDialog(false);
            resetRecording();
          }}
        />
      </CardContent>
    </Card>
  );
};
